import { useEffect, useState } from "react";
import { Send, MailPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "../lib/api";
import { Field, StatusBadge } from "./kit";

const parse = (text) => text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l) => {
  const cells = l.split(/[,\t;]/).map((c) => c.trim());
  const email = cells.find((c) => c.includes("@")) || "";
  return { email, name: cells.filter((c) => c !== email).join(" ") };
});

function SentList({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="mt-5" data-testid="review-requests-list"><span className="label">Requests sent</span>
      <div className="divide-y divide-slate-100 text-sm">{rows.map((r) => (
        <div key={r.id} className="py-2 flex justify-between gap-3" data-testid="review-request-row"><span className="min-w-0 truncate">{r.name || "—"} <span className="muted">· {r.email}</span></span>
          <span className="flex items-center gap-2 text-xs muted shrink-0">{fmtDate(r.sent_at)}<StatusBadge status={r.status} /></span></div>))}</div>
    </div>
  );
}

export function ReviewRequests() {
  const [d, setD] = useState(null);
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/provider/review-requests").then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const rows = parse(text);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/provider/review-requests", { customers: rows, note });
      toast.success(`Review request sent to ${data.sent} customer${data.sent === 1 ? "" : "s"}`);
      data.skipped.forEach((s) => toast.warning(`${s.email}: ${s.reason}`));
      setText(""); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="card p-5 mb-6" data-testid="review-requests">
      <h2 className="font-bold flex items-center gap-2"><MailPlus size={18} className="text-[#FF8166]" />Ask past customers for a review</h2>
      <p className="text-sm muted mt-1 mb-4">Each customer gets a personal one-time link. Their review is labelled "Past customer" and checked by our team before it appears. <b data-testid="review-requests-remaining">{d.remaining} of {d.limit}</b> requests left this month.</p>
      {!d.can_send ? <p className="text-sm text-amber-700" data-testid="review-requests-locked">You can ask for reviews once your profile is approved.</p> : <>
        <Field label="Customers (one per line: name, email)"><textarea className="input min-h-[110px] font-mono text-xs" value={text} onChange={(e) => setText(e.target.value)} placeholder={"Sarah Jones, sarah@example.co.uk\nTom Patel, tom@example.co.uk"} data-testid="review-requests-input" /></Field>
        <Field label="Personal note (optional)"><input className="input" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Thanks again for choosing us for your bathroom refit!" data-testid="review-requests-note" /></Field>
        <button onClick={send} disabled={busy || !rows.length || !d.remaining} className="btn-coral" data-testid="review-requests-send"><Send size={15} />{busy ? "Sending…" : `Send ${rows.length || ""} request${rows.length === 1 ? "" : "s"}`}</button>
      </>}
      <SentList rows={d.requests} />
    </section>
  );
}
